import React, { FC } from "react";
import Button, { ButtonProps } from "@mui/material/Button";
import Box from "@mui/material/Box";

export type PillTone = "amber" | "dark" | "outline";

const TONE_PROPS: Record<
  PillTone,
  { variant: ButtonProps["variant"]; color: ButtonProps["color"] }
> = {
  amber: { variant: "contained", color: "primary" },
  dark: { variant: "contained", color: "secondary" },
  outline: { variant: "outlined", color: "secondary" },
};

export interface PillButtonProps
  extends Omit<ButtonProps, "color" | "variant">,
    // MUI's Button is polymorphic; these let it be rendered as an anchor for
    // the external guide/PDF links without reaching for a generic signature.
    Pick<
      React.AnchorHTMLAttributes<HTMLAnchorElement>,
      "href" | "target" | "rel" | "download"
    > {
  tone?: PillTone;
  component?: React.ElementType;
  /** Appends the site's trailing arrow glyph. */
  withArrow?: boolean;
}

/**
 * The site's pill CTA. Radius, padding and weight come from the theme; this only
 * maps a tone to a (variant, color) pair and handles the arrow convention.
 *
 * The arrow is a plain text span rather than an `<Icon>` ligature so it inherits
 * Nunito Sans instead of pulling in the Material Icons font.
 */
const PillButton: FC<PillButtonProps> = ({
  tone = "amber",
  withArrow = false,
  endIcon,
  children,
  ...rest
}) => {
  const { variant, color } = TONE_PROPS[tone];

  return (
    <Button
      variant={variant}
      color={color}
      endIcon={
        endIcon ??
        (withArrow ? (
          <Box component="span" aria-hidden sx={{ ml: -0.5 }}>
            &#8594;
          </Box>
        ) : undefined)
      }
      {...rest}
    >
      {children}
    </Button>
  );
};

export default PillButton;
